import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Box, Collapse, Container, List, ListItem, Typography, useMediaQuery } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { graphql, useStaticQuery } from 'gatsby';
import * as React from 'react';

import Image from 'components/Image';
import Link from 'components/Link';
import useLiveData from 'hooks/useLiveData';
import amin from 'images/amin-worldwide-logo.svg';
import facebook from 'images/facebook.svg';
import instagram from 'images/instagram.svg';
import linkedin from 'images/linkedin.svg';
import batonRouge from 'images/locations/baton-rouge.svg';
import nashville from 'images/locations/nashville.svg';
import newOrleans from 'images/locations/new-orleans.svg';
import rosemaryBeach from 'images/locations/rosemary-beach.svg';
import hills1 from "images/lottie-fakes/hills-1.svg";
import twitter from 'images/x-social.svg';
import theme, { getColor } from 'utils/theme';

import { remCalc } from '../utils/theme';

const Footer = (props) => {
	const data = useStaticQuery(graphql`
		query footerMenu {
			allContentMenu(
				filter: {menuID: {eq: "footer"}, approved: {eq: true}, archived: {eq: false}, deleted: {eq: false}}
			) 
			{
				edges {
					node {
						pages
					}
				}
			}
			allStaffLocation(
				filter: {approved: {eq: true}, archived: {eq: false}, deleted: {eq: false}}
			) 
			{
				edges {
					node {
						name
						uri
					}
				}
			}
		}
	`);

	const { response: providerResponse } = useLiveData({
		type: 'ContentMenu',
		method: 'all',
		apiParams: {
			menuID: 'footer',
		},
		staticData: data,
	});

	const icons = [
		newOrleans,
		nashville,
		batonRouge,
		rosemaryBeach,
	];

	const { response: locationProviderResponse } = useLiveData({
		type: 'StaffLocation',
		method: 'all',
		staticData: data,
	});

	const links = providerResponse?.data[0]?.pages.filter(item => {
		if (item.visible === 'true' || item.visible === true) return true;
		return false;
	});

	const locations = locationProviderResponse?.data.map((location, index) => {
		return {
			name: location.name,
			uri: location.uri,
			icon: icons[index]
		}
	});

	const footerStyle = {
		position: 'relative',
		zIndex: 200,
		...theme.palette.footer,
		[theme.breakpoints.down('sm')]: {
			paddingTop: remCalc(60),
		},
		[theme.breakpoints.down('laptop')]: {
			background: 'linear-gradient(0deg, #0F2345 0%, #0F2345 60%)'
		},
		'.socialMediaLink': {
			filter: "brightness(0) saturate(100%) invert(66%) sepia(12%) saturate(701%) hue-rotate(153deg) brightness(85%) contrast(93%)",
			'&:hover': {
				filter: 'brightness(0) invert(75%) sepia(57%) saturate(431%) hue-rotate(347deg) brightness(103%) contrast(101%)',
			}
		}
	}

	const footerListStyle = {
		display: 'grid',
		gridTemplateColumns: 'repeat(3, 1fr)',
		gridAutoFlow: 'column',
		gridTemplateRows: 'repeat(4, 1fr)',
		gridColumnGap: 100,
		gridRowGap: 0,
		width: '100%',
		'> .MuiGrid2-root': {
			gridArea: '1 / 1 / 6 / 2'
		},
		[theme.breakpoints.down('laptop')]: {
			display: 'block',
			'.MuiListItem-root': {
				justifyContent: 'center',
				paddingTop: '16.5px',
				paddingBottom: '16.5px',
			}
		}
	}

	const bottomBannerInnerStyle = {
		paddingBottom: '26px',
		background: getColor('blue', 700),
		display: 'flex',
		columnGap: '16px',
		justifyContent: 'center',
		alignItems: 'center',
	}

	const linksMenuStyle = {
		[theme.breakpoints.up('laptop')]: {
			paddingLeft: '70px',
			paddingRight: '70px',
		}
	}

	const locationLinkStyle = {
		padding: '10px 0 75px',
		[theme.breakpoints.down('laptop')]: {
			paddingTop: 0,
			paddingBottom: '61px'
		},
		'@keyframes spin': {
			'from': { transform: 'rotate(0deg)' },
			'to': { transform: 'rotate(360deg)' }
		},
		'@keyframes enlarge': {
			'from': { transform: 'scale(1)' },
			'to': { transform: 'scale(1.1)' }
		},
		'& a': {
			display: 'flex',
			alignItems: 'center',
			gap: '11px',
			[theme.breakpoints.down('laptop')]: {
				flexDirection: 'column',
				columnGap: '11px',
			},
			'& img': {
				width: '38px',
				'transition': 'transform .2s ease-in-out',
			},
			[theme.breakpoints.up('laptop')]: {
				'&:hover': {
					textDecoration: 'none',
					'& img': {
						'&:not(.nola)': {
							animation: 'spin .35s ease-in-out 1'
						},
						'&.nola': {
							transform: 'scale(1.1)'
						}
					}
				}
			}
		}
	}

	const hillsStyling = {
		position: 'relative',
		width: '100%',
		marginBottom: '-8px',
	}

	const isMobile = useMediaQuery(theme.breakpoints.up('laptop'));
	const [open, setOpen] = React.useState(true);

	const handleClick = () => {
		setOpen(!open);
	};

	React.useEffect(() => {
		setOpen(isMobile);
	}, [isMobile]);


	return (
		<>
			<Image style={hillsStyling} src={hills1} />
			<Box component="footer" className='footer-nav' sx={footerStyle}>
				<Container>
					<Grid flexDirection={{ xs: 'column', md: 'row' }} justifyContent={{ md: "space-between" }} alignItems="center" container spacing={3}>
						{locations.map((location, index) => (
							<Grid key={location.name} sx={locationLinkStyle} size={{ xs: 12, sm: 12, md: 3 }}>
								<Link href={location.uri} activeClassName="active" className="locationLink">
									<Image className={location.name === 'New Orleans' ? 'nola' : ''} width="36" height="38" src={location.icon} alt="" />
									<Typography variant='footerLocation'>{location.name}</Typography>
								</Link>
							</Grid>
						))}
					</Grid>
				</Container>
				<Container>
					<Grid sx={linksMenuStyle} container spacing={3}>
						<List sx={footerListStyle}>
							{links.map((link) => {
								if (link.children && link.children.length > 0) {
									return (
										<Grid key={link.id}>
											<ListItem>
												<Link href={link.href} activeClassName="active"><Typography variant='footerLinkParent'>{link.text}</Typography></Link>
												<Box onClick={handleClick} sx={{ display: { xs: 'block', md: 'none' } }}>
													{open ? <ExpandLess width={'32px'} /> : <ExpandMore width={'32px'} />}
												</Box>
											</ListItem>
											<Collapse in={open}>
												{link.children.map((child) => (
													<ListItem key={child.id}>
														<Link href={child.href} activeClassName="active"><Typography variant='footerLinkChild'>{child.text}</Typography></Link>
													</ListItem>
												))}
											</Collapse>
										</Grid>
									);
								}
								return (
									<ListItem key={link.id}>
										<Link href={link.href} activeClassName="active"><Typography variant='footerLinkParent'>{link.text}</Typography></Link>
									</ListItem>
								)
							})}
							<ListItem sx={{ paddingTop: '0', paddingBottom: '0' }}>
								<Link href="/careers/" activeClassName="active"><Typography variant='footerLinkParent'>Join Our Team</Typography></Link>
							</ListItem>
							<ListItem sx={{ paddingTop: '0', paddingBottom: '0' }}>
								<Link href="/contact/" activeClassName="active"><Typography variant='footerLinkParent'>Contact Us</Typography></Link>
							</ListItem>
							<ListItem sx={{ paddingTop: '0', paddingBottom: '0' }}>
								<Link href="/privacy/" activeClassName="active"><Typography variant='footerLinkParent'>Privacy Policy &amp; Terms</Typography></Link>
							</ListItem>
						</List>
					</Grid>
				</Container>
				<Container sx={{ height: "100%", display: "flex", flexDirection: { md: 'row', xs: 'column' }, paddingTop: { xs: '2rem', md: '120px' }, paddingBottom: "2rem", justifyContent: "space-between", alignItems: "center" }} disableGutters={true}>
					<Grid container justifyContent={{ xs: "center", md: "flex-start" }}>
						<Grid xs={12} sm={12} md={12} style={{ display: 'flex', flexDirection: 'column' }}>
							<Typography
								style={{
									fontSize: '1rem',
									fontWeight: 600,
									lineHeight: '1.25',
									textTransform: 'capitalize',
								}}
							>
								A Member of
							</Typography>
							<Link href="https://www.aminworldwide.com/" target="_blank"><Image width="150" height="50" src={amin} alt="Link to AMIN Worldwide website" /></Link>
						</Grid>
					</Grid>
					<Grid container alignItems="center" justifyContent={{ xs: "center", md: 'flex-start' }} style={{ display: 'flex', gap: '40px', padding: '0', marginTop: '1rem', marginBottom: '1rem' }}>
						<Grid xs={2} sm={2} style={{ display: 'flex', justifyContent: 'center' }}>
							<Link className="socialMediaLink" href="https://www.instagram.com/wearezehnder/" target="_blank"><Image width="28" height="28" src={instagram} alt="Link to Instagram social media account" /></Link>
						</Grid>
						<Grid xs={2} sm={2} style={{ display: 'flex', justifyContent: 'center' }}>
							<Link className="socialMediaLink" href="https://x.com/wearezehnder/" target="_blank"><Image width="22" height="24" src={twitter} alt="Link to X social media account" /></Link>
						</Grid>
						<Grid xs={2} sm={2} style={{ display: 'flex', justifyContent: 'center' }}>
							<Link className="socialMediaLink" href="https://www.facebook.com/wearezehnder/" target="_blank"><Image width="13" height="26" src={facebook} alt="Link to Facebook social media account" /></Link>
						</Grid>
						<Grid xs={2} sm={2} style={{ display: 'flex', justifyContent: 'center' }}>
							<Link className="socialMediaLink" href="https://www.linkedin.com/company/wearezehnder/" target="_blank"><Image width="28" height="28" src={linkedin} alt="Link to LinkedIn social media account" /></Link>
						</Grid>
					</Grid>
					<Typography variant="copyright" component="div">© {(new Date()).getFullYear()} Zehnder Communications, Inc.</Typography>
				</Container>
				<Box>
					<Box sx={bottomBannerInnerStyle}>
						<Container>
							<Grid container justifyContent="center" alignItems="center">
								<Grid>
									<Typography variant="legal" component="small">
										This site is protected by reCAPTCHA and the Google <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">Privacy Policy</a> and <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer">Terms of Service</a> apply.
									</Typography>
								</Grid>
							</Grid>
						</Container>
					</Box>
				</Box>
			</Box>
		</>
	)
}

export default Footer;